import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "@radix-ui/react-icons";
import Cookies from "js-cookie";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { AxiosError } from "axios";

import { cn } from "@/lib/utils";
import { getDatesWithDayNumber } from "@/utils";

import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Calendar } from "@/components/ui/calendar/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion/accordion";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { HolidayListTable } from "./holiday-list-table/holiday-list-table";

import { editHolidayList } from "@/config/mutation/admin";
import { getOneHolidayListQueryOptions } from "@/config/queries/query-options";
import { Separator } from "@/components/ui/separator/separator";
import { HalfDaysTable } from "./half-days-table/half-days-table";

function findOldestAndNewestDates(
  holidays: {
    holidayName: string;
    description: string;
    date: Date;
  }[]
): Date[] {
  if (holidays.length === 0) {
    throw new Error("Array is empty");
  }

  let oldestDate: Date = holidays[0].date;
  let newestDate: Date = holidays[0].date;

  for (const holiday of holidays) {
    if (holiday.date < oldestDate) {
      oldestDate = holiday.date;
    }
    if (holiday.date > newestDate) {
      newestDate = holiday.date;
    }
  }

  return [oldestDate, newestDate];
}

const formSchema = z.object({
  name: z.string().min(1, {
    message: "Holiday list name required",
  }),
  fromDate: z.date().optional(),
  toDate: z.date().optional(),
  totalHolidays: z.number().optional(),
  weeklyHoliday: z.number().optional(),
  holidays: z.array(
    z.object({
      holidayName: z.string(),
      date: z.date(),
      description: z.string(),
    })
  ),
  halfDays: z
    .array(
      z.object({
        dayOfWeek: z.number(),
        minimumHours: z.number(),
      })
    )
    .optional(),
});

export type FormValues = z.infer<typeof formSchema>;

export const EditHolidayListForm = () => {
  const adminKey = Cookies.get("key") as string;
  const { holidayListId } = useParams() as {
    holidayListId: string;
  };

  const queryClient = useQueryClient();
  // const navigate = useNavigate();

  const { data, isPending: isHolidayListPending } = useQuery(
    getOneHolidayListQueryOptions(adminKey, holidayListId)
  );

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      fromDate: undefined,
      toDate: undefined,
      totalHolidays: 0,
      holidays: [],
      halfDays: [],
      weeklyHoliday: undefined,
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: editHolidayList(adminKey),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    mutate(
      {
        name: values.name,
        holidays: values.holidays,
        halfDays: values.halfDays,
        holidayListId,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["holidays"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["holiday", holidayListId],
          });

          toast.success("Holiday List Edited!");

          // navigate("/admin/holidays", {
          //   replace: true,
          // });
        },

        onError: (error) => {
          console.log("ERROR WHILE EDITING HOLIDAY LIST", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  const handleGenerateHolidays = () => {
    if (
      form.getValues("fromDate") === undefined ||
      form.getValues("toDate") === undefined ||
      form.getValues("weeklyHoliday") === undefined
    ) {
      console.log("In Check");
      console.log(form.getValues("weeklyHoliday"));
      return;
    }

    const result = getDatesWithDayNumber(
      form.getValues("fromDate") as Date,
      form.getValues("toDate") as Date,
      form.getValues("weeklyHoliday") as number
    );

    const formattedValue = result.map((hol) => ({
      holidayName: hol.name,
      date: hol.date,
      description: hol.name,
    }));

    const prevValues = form.getValues("holidays");

    const all = [...formattedValue, ...prevValues].sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );

    form.setValue("holidays", all);
  };

  useEffect(() => {
    if (!isHolidayListPending && data && data?.foundList) {
      form.setValue("name", data?.foundList?.name);
      const holidays = data?.foundList?.holidays?.map((h) => {
        return {
          ...h,
          date: new Date(h?.date),
        };
      });
      form.setValue("holidays", holidays);
      form.setValue("halfDays", data?.foundList?.halfDays);
      form.setValue("totalHolidays", holidays.length);

      const [oldDate, newDate] = findOldestAndNewestDates(
        data?.foundList?.holidays
      );

      form.setValue("fromDate", new Date(oldDate));
      form.setValue("toDate", new Date(newDate));
    }
  }, [data, isHolidayListPending]);

  return (
    <div className="h-full rounded-md relative pb-10">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex items-center justify-between sticky top-0 px-4 z-30 rounded-md bg-background">
            <h3 className="text-2xl font-medium">New Holiday List</h3>

            <Button type="submit" size="sm" disabled={isPending}>
              Save
            </Button>
          </div>

          <div className="py-5 px-5 mt-5 rounded-b-md">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Holiday List Name</FormLabel>
                    <FormControl>
                      <Input disabled={isPending} type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator className="my-5 bg-gray-500" />

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="fromDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col space-y-0 gap-3 mt-1">
                    <FormLabel>From Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            disabled={isPending}
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(day) => {
                            field.onChange(day);
                            // if (isFirstTime.current) {
                            //   const nextYearDate = getNextYearDate(day as Date);
                            //   form.setValue("toDate", nextYearDate);
                            //   isFirstTime.current = false;
                            // }
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="toDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col space-y-0 gap-3 mt-1">
                    <FormLabel>To Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            disabled={isPending}
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(day) => {
                            field.onChange(day);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {form.getValues("fromDate") && form.getValues("toDate") && (
              <div className="border-t mt-4">
                <Accordion type="single" collapsible disabled={isPending}>
                  <AccordionItem value="item-1" className="">
                    <AccordionTrigger>Add Weekly Holidays</AccordionTrigger>
                    <AccordionContent>
                      <div className="px-3">
                        <div className="grid grid-cols-1 lg:grid-cols-2">
                          <FormField
                            control={form.control}
                            name="weeklyHoliday"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Weekly Off</FormLabel>
                                <Select
                                  value={String(field.value)}
                                  onValueChange={(value) => {
                                    field.onChange(Number(value));
                                  }}
                                >
                                  <SelectTrigger className="">
                                    <SelectValue placeholder="Weekly Day" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="0">Sunday</SelectItem>
                                    <SelectItem value="1">Monday</SelectItem>
                                    <SelectItem value="2">Tuesday</SelectItem>
                                    <SelectItem value="3">Wednesday</SelectItem>
                                    <SelectItem value="4">Thursday</SelectItem>
                                    <SelectItem value="5">Friday</SelectItem>
                                    <SelectItem value="6">Saturday</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="flex items-center mt-3">
                          <Button
                            size="sm"
                            type="button"
                            onClick={handleGenerateHolidays}
                          >
                            Add to Holidays
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}

            <HolidayListTable />
          </div>

          <Separator className="my-5 bg-gray-500" />

          <div>
            <HalfDaysTable />
          </div>
        </form>
      </Form>
    </div>
  );
};
